import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { get, put } from "src/services/api";
import { SUCCESS_MESSAGE, ERROR_MESSAGE } from "./DeploymentSettings.consts";

const useDeploymentSettings = (setFeedbackConfig, isAdminContext = true) => {
  const [initialData, setInitialData] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [errorTexts, setErrorTexts] = useState({});
  const [rowInfo, setRowInfo] = useState(null);

  const deploymentSettingsEndpoint = (assistantId) =>
    (isAdminContext
      ? `/admin/assistants/${assistantId}/deployment-settings`
      : `/assistants/${assistantId}/settings/deployment`);

  const fetchSettingsMutation = useMutation({
    mutationFn: async (assistantId) => await get({ endpoint: deploymentSettingsEndpoint(assistantId) }),
    onSuccess: (data) => {
      setInitialData(data);
      setCurrentData(data);
    },
    onError: () => {
      setFeedbackConfig?.({ show: true, message: ERROR_MESSAGE, severity: "error" });
    }
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async ({ assistantId, data }) => await put({ endpoint: deploymentSettingsEndpoint(assistantId), data }),
    onSuccess: () => {
      setInitialData(currentData);
      setFeedbackConfig?.({ show: true, message: SUCCESS_MESSAGE, severity: "success" });
    },
    onError: () => {
      setFeedbackConfig?.({ show: true, message: ERROR_MESSAGE, severity: "error" });
    }
  });

  const areSettingsChanged = useMemo(() => {
    if (!initialData || !currentData) return false;
    const a = JSON.stringify({
      minReplicas: initialData?.minReplicas,
      memory: initialData?.memory,
      forceMinReplicas: initialData?.forceMinReplicas
    });
    const b = JSON.stringify({
      minReplicas: currentData?.minReplicas,
      memory: currentData?.memory,
      forceMinReplicas: currentData?.forceMinReplicas
    });
    return a !== b;
  }, [initialData, currentData]);

  const fetchSettings = async (row) => {
    if (!row?.id) return;
    setRowInfo(row);
    await fetchSettingsMutation.mutateAsync(row.id);
  };

  const clear = () => {
    setInitialData(null);
    setCurrentData(null);
    setErrorTexts({});
    setRowInfo(null);
  };

  const setField = (field, value) => {
    setCurrentData(prev => ({
      ...(prev || {}),
      [field]: value
    }));
  };

  const submit = async () => {
    if (!rowInfo?.id || !currentData) return false;
    try {
      await updateSettingsMutation.mutateAsync({
        assistantId: rowInfo.id,
        data: {
          minReplicas: currentData.minReplicas,
          memory: currentData.memory,
          forceMinReplicas: !!currentData.forceMinReplicas
        }
      });
      return true;
    } catch {
      return false;
    }
  };

  const props = useMemo(() => ({
    minReplicas: {
      value: currentData?.minReplicas ?? "",
      handleChange: (val) => setField("minReplicas", val === "" ? "" : Number(val)),
      errorText: errorTexts.minReplicas,
      setErrorText: (val) => setErrorTexts(prev => ({ ...prev, minReplicas: val }))
    },
    cpu: {
      value: currentData?.cpu ?? "",
      handleChange: (val) => setField("cpu", val),
      errorText: errorTexts.cpu,
      setErrorText: (val) => setErrorTexts(prev => ({ ...prev, cpu: val }))
    },
    memory: {
      value: currentData?.memory ? currentData.memory.replace("Gi", "") : "",
      handleChange: (val) => setField("memory", val === "" ? "" : `${val}Gi`),
      errorText: errorTexts.memory,
      setErrorText: (val) => setErrorTexts(prev => ({ ...prev, memory: val }))
    },
    dedicated: {
      value: currentData?.dedicated ? "true" : "false",
      handleChange: () => {}, // No-op since it's always disabled
      errorText: errorTexts.dedicated,
      setErrorText: (val) => setErrorTexts(prev => ({ ...prev, dedicated: val }))
    },
    forceMinReplicas: {
      value: currentData?.forceMinReplicas ?? false,
      handleChange: (val) => setField("forceMinReplicas", val),
      errorText: errorTexts.forceMinReplicas,
      setErrorText: (val) => setErrorTexts(prev => ({ ...prev, forceMinReplicas: val }))
    }
  }), [currentData, errorTexts, setField]);

  return {
    isLoading: fetchSettingsMutation.isPending,
    isSubmitting: updateSettingsMutation.isPending,
    areSettingsChanged,
    fetchSettings,
    clear,
    submit,
    rowInfo,
    initialData,
    currentData,
    fieldProps: {
      ...props,
      draftExists: currentData?.draftExists
    }
  };
};

export default useDeploymentSettings;

